import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Target, TrendingUp, Zap, DollarSign, Bot, Star, CheckCircle2, ArrowUp, ArrowDown, Users, Search, MessageSquare, Sparkles, Shield, Clock, Phone, ExternalLink, BarChart3, PieChart, Activity } from "lucide-react";
import { useState, useEffect } from "react";

// AI Chat with Sponsored Ad Mockup
const AIAdInChatMockup = () => {
  const [showAd, setShowAd] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  
  useEffect(() => {
    const timer1 = setTimeout(() => setShowAd(true), 400);
    const timer2 = setTimeout(() => setShowDetails(true), 800);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <div className="relative h-full">
      <div className="glass rounded-xl overflow-hidden h-full flex flex-col border border-border/50">
        {/* Browser Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 bg-secondary/30">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-destructive/60" />
            <div className="w-3 h-3 rounded-full bg-accent/60" />
            <div className="w-3 h-3 rounded-full bg-primary/60" />
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3 w-3 text-accent" />
              <span className="text-xs text-muted-foreground">ChatGPT</span>
            </div>
          </div>
          <div className="w-12" />
        </div>
        
        <div className="p-4 space-y-3 flex-1 overflow-hidden">
          {/* User Question */}
          <div className="flex justify-end">
            <div className="bg-primary/20 rounded-xl rounded-br-sm px-3 py-2 max-w-[85%]">
              <p className="text-xs">Find the best personal injury lawyer in Dallas</p>
            </div>
          </div>
          
          {/* AI Response with Sponsored Ad */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: showAd ? 1 : 0, y: showAd ? 0 : 10 }}
            className="space-y-2"
          >
            <div className="flex gap-2">
              <div className="w-7 h-7 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                <Sparkles className="h-3.5 w-3.5 text-accent" />
              </div>
              <div className="space-y-2 flex-1">
                <p className="text-xs text-muted-foreground">Here's my top recommendation based on ratings and reviews:</p>
                
                {/* Sponsored Result - Enhanced */}
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: showDetails ? 1 : 0 }}
                  className="relative"
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/30 to-accent/30 rounded-xl blur-sm" />
                  <div className="relative bg-card border-2 border-primary/50 rounded-xl overflow-hidden">
                    <div className="bg-primary/10 px-3 py-1.5 flex items-center justify-between border-b border-primary/20">
                      <span className="text-[9px] font-bold text-primary uppercase tracking-wider flex items-center gap-1">
                        <Sparkles className="h-2.5 w-2.5" /> SPONSORED
                      </span>
                      <span className="text-[9px] text-accent font-medium">Best Match</span>
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-bold flex items-center gap-1.5">
                            Anderson & Partners Law
                            <ExternalLink className="h-3 w-3 text-muted-foreground" />
                          </p>
                          <div className="flex items-center gap-1.5 mt-1">
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className="h-3 w-3 fill-accent text-accent" />
                              ))}
                            </div>
                            <span className="text-[10px] text-muted-foreground">4.9 (200+)</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 bg-accent/10 text-accent text-[9px] px-1.5 py-0.5 rounded">
                          <Shield className="h-2.5 w-2.5" />
                          <span>Verified</span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground bg-secondary/50 rounded px-2 py-1.5">
                          <CheckCircle2 className="h-3 w-3 text-accent" />
                          <span>Free Consultation</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground bg-secondary/50 rounded px-2 py-1.5">
                          <CheckCircle2 className="h-3 w-3 text-accent" />
                          <span>No Win No Fee</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground bg-secondary/50 rounded px-2 py-1.5">
                          <Clock className="h-3 w-3 text-accent" />
                          <span>24/7 Available</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground bg-secondary/50 rounded px-2 py-1.5">
                          <Phone className="h-3 w-3 text-accent" />
                          <span>Same Day Response</span>
                        </div>
                      </div>

                      <button className="w-full bg-primary text-primary-foreground text-[10px] font-semibold py-2 rounded-lg flex items-center justify-center gap-1.5">
                        <Phone className="h-3 w-3" />
                        SCHEDULE FREE CONSULTATION
                      </button>
                    </div>
                  </div>
                </motion.div>
                
                {/* Other results faded */}
                <div className="opacity-30 space-y-1.5">
                  <div className="bg-secondary/30 rounded-lg p-2 flex items-center justify-between">
                    <p className="text-[10px]">Smith Legal Group</p>
                    <span className="text-[9px] text-muted-foreground">4.5 ★</span>
                  </div>
                  <div className="bg-secondary/30 rounded-lg p-2 flex items-center justify-between">
                    <p className="text-[10px]">Dallas Law Associates</p>
                    <span className="text-[9px] text-muted-foreground">4.3 ★</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// ROI Comparison Mockup - Enhanced
const ROIComparisonMockup = () => {
  const [showData, setShowData] = useState(false);
  const [animatedValues, setAnimatedValues] = useState({ googleCPL: 0, aiCPL: 0, googleCTR: 0, aiCTR: 0 });
  
  useEffect(() => {
    const timer = setTimeout(() => setShowData(true), 400);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (showData) {
      const duration = 1000;
      const steps = 30;
      let step = 0;
      const interval = setInterval(() => {
        step++;
        const progress = step / steps;
        setAnimatedValues({
          googleCPL: Math.round(85 * progress),
          aiCPL: Math.round(22 * progress),
          googleCTR: Number((2.1 * progress).toFixed(1)),
          aiCTR: Number((8.7 * progress).toFixed(1)),
        });
        if (step >= steps) clearInterval(interval);
      }, duration / steps);
      return () => clearInterval(interval);
    }
  }, [showData]);

  return (
    <div className="relative h-full">
      <div className="glass rounded-xl p-4 border border-accent/30 h-full flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
              <BarChart3 className="h-4 w-4 text-accent" />
            </div>
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider">ROI Comparison</span>
              <p className="text-[10px] text-muted-foreground">Real campaign data</p>
            </div>
          </div>
          <span className="text-[9px] bg-accent/20 text-accent px-2 py-1 rounded-full font-medium">LIVE DATA</span>
        </div>
        
        <div className="space-y-3 flex-1">
          {/* Google Ads Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: showData ? 1 : 0, x: showData ? 0 : -20 }}
            transition={{ delay: 0.1 }}
            className="bg-secondary/50 rounded-xl p-3"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-muted-foreground/20 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-muted-foreground">G</span>
                </div>
                <span className="text-xs font-medium">Google Ads</span>
              </div>
              <span className="text-[9px] text-destructive flex items-center gap-1 bg-destructive/10 px-1.5 py-0.5 rounded">
                <ArrowDown className="h-3 w-3" />
                Saturated
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-secondary/50 rounded-lg p-2">
                <p className="text-lg font-bold text-muted-foreground">${animatedValues.googleCPL}</p>
                <p className="text-[9px] text-muted-foreground">Cost/Lead</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-2">
                <p className="text-lg font-bold text-muted-foreground">{animatedValues.googleCTR}%</p>
                <p className="text-[9px] text-muted-foreground">CTR</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-2">
                <p className="text-lg font-bold text-muted-foreground">High</p>
                <p className="text-[9px] text-muted-foreground">Competition</p>
              </div>
            </div>
          </motion.div>
          
          {/* AI Ads Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: showData ? 1 : 0, x: showData ? 0 : -20 }}
            transition={{ delay: 0.3 }}
            className="bg-accent/10 rounded-xl p-3 border-2 border-accent/40"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-accent/20 flex items-center justify-center">
                  <Sparkles className="h-3.5 w-3.5 text-accent" />
                </div>
                <span className="text-xs font-medium">AI Platforms</span>
              </div>
              <span className="text-[9px] text-accent flex items-center gap-1 bg-accent/20 px-1.5 py-0.5 rounded">
                <ArrowUp className="h-3 w-3" />
                Growing
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-accent/10 rounded-lg p-2 border border-accent/20">
                <p className="text-lg font-bold text-accent">${animatedValues.aiCPL}</p>
                <p className="text-[9px] text-muted-foreground">Cost/Lead</p>
              </div>
              <div className="bg-accent/10 rounded-lg p-2 border border-accent/20">
                <p className="text-lg font-bold text-accent">{animatedValues.aiCTR}%</p>
                <p className="text-[9px] text-muted-foreground">CTR</p>
              </div>
              <div className="bg-accent/10 rounded-lg p-2 border border-accent/20">
                <p className="text-lg font-bold text-accent">Low</p>
                <p className="text-[9px] text-muted-foreground">Competition</p>
              </div>
            </div>
          </motion.div>
          
          {/* Summary */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: showData ? 1 : 0 }}
            transition={{ delay: 0.5 }}
            className="bg-accent/5 rounded-xl p-3 border border-accent/20 mt-auto"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-accent" />
                <span className="text-sm font-semibold">74% Lower Cost</span>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-accent">4x</p>
                <p className="text-[9px] text-muted-foreground">Higher CTR</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// Lead Flow Mockup - Enhanced
const LeadFlowMockup = () => {
  const [leads, setLeads] = useState([
    { name: "Michael R.", type: "Plumbing Emergency", platform: "ChatGPT", time: "2 min ago", initials: "MR", value: "$340" },
    { name: "Jennifer L.", type: "Legal Consultation", platform: "Gemini", time: "5 min ago", initials: "JL", value: "$1,200" },
    { name: "David K.", type: "HVAC Service", platform: "Perplexity", time: "8 min ago", initials: "DK", value: "$520" },
  ]);
  const [totalValue, setTotalValue] = useState(4850);
  
  useEffect(() => {
    const interval = setInterval(() => {
      const newLeads = [
        { name: "Sarah W.", type: "Dental Checkup", platform: "ChatGPT", time: "Just now", initials: "SW", value: "$180" },
        { name: "Lisa M.", type: "Home Cleaning", platform: "Perplexity", time: "Just now", initials: "LM", value: "$95" },
        { name: "Robert S.", type: "Insurance Quote", platform: "Gemini", time: "Just now", initials: "RS", value: "$280" },
      ];
      const newLead = newLeads[Math.floor(Math.random() * newLeads.length)];
      setLeads(prev => [newLead, prev[0], prev[1]]);
      setTotalValue(prev => prev + parseInt(newLead.value.replace(/[$,]/g, '')));
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative h-full">
      <div className="glass rounded-xl p-4 border border-accent/30 h-full flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
              <Activity className="h-4 w-4 text-accent" />
            </div>
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider">Live Lead Feed</span>
              <p className="text-[10px] text-muted-foreground">Real-time conversions</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-accent/10 px-2 py-1 rounded-full">
            <div className="w-2 h-2 rounded-full bg-accent animate-ping" />
            <span className="text-[9px] text-accent font-medium">LIVE</span>
          </div>
        </div>
        
        <div className="space-y-2 flex-1">
          {leads.map((lead, i) => (
            <motion.div
              key={`${lead.name}-${i}-${Date.now()}`}
              initial={{ opacity: 0, x: -20, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="bg-secondary/50 rounded-xl p-3 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-accent/20 flex items-center justify-center">
                  <span className="text-[10px] font-semibold text-accent">{lead.initials}</span>
                </div>
                <div>
                  <p className="text-xs font-semibold">{lead.name}</p>
                  <p className="text-[10px] text-muted-foreground">{lead.type}</p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-[9px] bg-accent/20 text-accent px-2 py-0.5 rounded-full">{lead.platform}</span>
                <p className="text-[10px] text-accent font-medium mt-1">{lead.value}</p>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="mt-3 pt-3 border-t border-border/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Today's Revenue</span>
            <span className="text-xl font-bold text-accent">${totalValue.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-muted-foreground">23 leads generated</span>
            <span className="text-accent font-medium flex items-center gap-1">
              <ArrowUp className="h-3 w-3" /> +34% vs yesterday
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Search Intent Mockup - Enhanced
const SearchIntentMockup = () => {
  const queries = [
    { text: "best plumber near me for emergency", intent: "High", category: "Service" },
    { text: "top rated dentist accepting new patients", intent: "Very High", category: "Healthcare" },
    { text: "affordable lawyer for car accident", intent: "Very High", category: "Legal" },
    { text: "commercial cleaning service downtown", intent: "High", category: "B2B" },
  ];
  const [currentQuery, setCurrentQuery] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuery(prev => (prev + 1) % queries.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative h-full">
      <div className="glass rounded-xl p-4 border border-accent/30 h-full flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
              <Target className="h-4 w-4 text-accent" />
            </div>
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider">High-Intent Searches</span>
              <p className="text-[10px] text-muted-foreground">Ready-to-buy customers</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-3 flex-1">
          <div className="bg-secondary/50 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">People are asking AI:</p>
              <span className="text-[9px] bg-accent/20 text-accent px-1.5 py-0.5 rounded">{queries[currentQuery].category}</span>
            </div>
            <motion.p
              key={currentQuery}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm font-medium text-foreground"
            >
              "{queries[currentQuery].text}"
            </motion.p>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-accent/10 rounded-lg p-3 border border-accent/20 text-center">
              <p className="text-2xl font-bold text-accent">92%</p>
              <p className="text-[9px] text-muted-foreground">Ready to Buy</p>
            </div>
            <div className="bg-accent/10 rounded-lg p-3 border border-accent/20 text-center">
              <p className="text-2xl font-bold text-accent">4x</p>
              <p className="text-[9px] text-muted-foreground">Higher Intent</p>
            </div>
          </div>
          
          <div className="flex items-start gap-2 p-2.5 bg-secondary/30 rounded-xl">
            <MessageSquare className="h-4 w-4 text-accent shrink-0 mt-0.5" />
            <span className="text-[10px] text-muted-foreground leading-relaxed">These users are actively looking for solutions — not just browsing</span>
          </div>
          
          <div className="bg-accent/5 rounded-xl p-3 border border-accent/20 mt-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium">Purchase Intent</span>
              <span className="text-sm font-bold text-accent">{queries[currentQuery].intent}</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <motion.div 
                key={currentQuery}
                initial={{ width: 0 }}
                animate={{ width: queries[currentQuery].intent === "Very High" ? "95%" : "85%" }}
                transition={{ duration: 0.5 }}
                className="h-full bg-accent rounded-full" 
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cost Savings Mockup - Enhanced
const CostSavingsMockup = () => {
  const [savings, setSavings] = useState(0);
  const [showBreakdown, setShowBreakdown] = useState(false);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setSavings(prev => Math.min(prev + 127, 3847));
    }, 80);
    const timer = setTimeout(() => setShowBreakdown(true), 1500);
    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="relative h-full">
      <div className="glass rounded-xl p-4 border border-accent/30 h-full flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
              <DollarSign className="h-4 w-4 text-accent" />
            </div>
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider">Cost Savings</span>
              <p className="text-[10px] text-muted-foreground">Monthly comparison</p>
            </div>
          </div>
          <span className="text-[9px] bg-accent/20 text-accent px-2 py-1 rounded-full font-medium">AVG CLIENT</span>
        </div>
        
        <div className="text-center py-3 flex-1 flex flex-col justify-center">
          <motion.p
            className="text-4xl font-bold text-accent"
            initial={{ scale: 0.5 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
          >
            ${savings.toLocaleString()}
          </motion.p>
          <p className="text-xs text-muted-foreground mt-1">Saved per month vs Google Ads</p>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: showBreakdown ? 1 : 0, y: showBreakdown ? 0 : 10 }}
            className="mt-3 inline-flex items-center justify-center gap-2 bg-accent/10 px-3 py-1.5 rounded-full"
          >
            <TrendingUp className="h-3.5 w-3.5 text-accent" />
            <span className="text-xs text-accent font-medium">77% Cost Reduction</span>
          </motion.div>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs bg-secondary/30 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded bg-muted-foreground/20 flex items-center justify-center">
                <span className="text-[9px] font-bold text-muted-foreground">G</span>
              </div>
              <span className="text-muted-foreground">Google Ads</span>
            </div>
            <div className="text-right">
              <span className="line-through text-muted-foreground">$5,000</span>
              <span className="text-[9px] text-muted-foreground block">per month</span>
            </div>
          </div>
          <div className="flex items-center justify-between text-xs bg-accent/10 rounded-lg p-3 border border-accent/20">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded bg-accent/20 flex items-center justify-center">
                <Sparkles className="h-3 w-3 text-accent" />
              </div>
              <span className="text-foreground font-medium">AI Ads</span>
            </div>
            <div className="text-right">
              <span className="text-accent font-bold">$1,153</span>
              <span className="text-[9px] text-muted-foreground block">per month</span>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 pt-2">
            <CheckCircle2 className="h-4 w-4 text-accent" />
            <span className="text-xs font-medium">Same leads, fraction of the cost</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Benefits = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const benefits = [
    {
      icon: Trophy,
      title: "First-Mover Advantage",
      description: "Be visible on AI platforms before your competitors even know they exist. Early advertisers dominate search results.",
      Mockup: AIAdInChatMockup,
    },
    {
      icon: TrendingUp,
      title: "Better ROI Than Google",
      description: "AI search users have higher purchase intent. Get more leads for less money compared to saturated Google Ads.",
      Mockup: ROIComparisonMockup,
    },
    {
      icon: Zap,
      title: "Instant Lead Flow",
      description: "When someone asks AI for a recommendation, your business appears first. Leads flow directly to you 24/7.",
      Mockup: LeadFlowMockup,
    },
    {
      icon: Target,
      title: "High-Intent Customers",
      description: "AI users ask specific questions when they're ready to buy. No more wasted clicks from casual browsers.",
      Mockup: SearchIntentMockup,
    },
    {
      icon: DollarSign,
      title: "Lower Ad Costs",
      description: "Less competition means lower cost per click. Get the same results while spending 60-70% less on ads.",
      Mockup: CostSavingsMockup,
    },
  ];

  const ActiveMockup = benefits[activeIndex].Mockup;

  return (
    <section className="py-12 md:py-16 relative">
      <div className="container mx-auto px-4 max-w-[1280px] relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-4 mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            Why Advertise on <span className="text-gradient">AI Platforms</span>?
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            The future of search is AI. Here's why smart businesses are moving their ad budgets to ChatGPT, Gemini & Perplexity.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Left side - Benefits list */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-3"
          >
            {benefits.map((benefit, index) => (
              <div
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`glass rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                  activeIndex === index 
                    ? 'border-accent/50 shadow-lg shadow-accent/10 bg-accent/5' 
                    : 'hover:border-border/80'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                    activeIndex === index ? 'bg-accent text-accent-foreground' : 'bg-accent/10 text-accent'
                  }`}>
                    <benefit.icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-base font-semibold mb-1 transition-colors ${
                      activeIndex === index ? 'text-accent' : ''
                    }`}>
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Right side - Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="min-h-[500px]"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                <ActiveMockup />
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;
